import React, { useEffect, useState } from 'react'

import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { useTranslation } from 'react-i18next'

import ControlArea from '@components/myScores/ControlArea'
import RemoveScoresButton from '@components/myScores/ControlArea/RemoveScoresButton'
import ScoresView from '@components/myScores/ScoresView'
import { ScoreViewOptions } from '@components/myScores/utils/types'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import NonIdealState from '@components/shared/NonIdealState'
import {
  TEMPLATE_COPIES_PAGE_COUNT,
  TemplateCopiesResponse,
} from '@pages/home/loaders/template.loader'
import { usePageTitle } from '@providers/LayoutProvider'
import SelectionProvider from '@providers/SelectionProvider'
import ApiRoutes from '@utils/api-routes'
import { ViewType } from '@utils/global'
import http from '@utils/http'
import Storage from '@utils/storage'

interface Props {
  initialData: TemplateCopiesResponse
  order: string | null
}

const TemplateCopiesView: React.FC<Props> = ({ initialData, order }) => {
  const { t } = useTranslation()
  usePageTitle(
    t('titles.scoresFromTemplateName', {
      name: initialData.template.title,
    })
  )

  const [loading, setLoading] = useState(false)
  const [scoresData, setScoresData] = useState(initialData)
  const [layoutType, setLayoutType] = useState(Storage.scoresViewType)
  const setViewType = (type: ViewType): void => {
    setLayoutType(type)
    Storage.scoresViewType = type
  }
  const { scores, remaining, startIndex } = scoresData

  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    async () => {
      if (!remaining || loading) return

      setLoading(true)
      const [newResp] = await http.post<TemplateCopiesResponse>(
        ApiRoutes.members.templateCopiesPath(),
        {
          order,
          max_count: TEMPLATE_COPIES_PAGE_COUNT,
          start_index: startIndex,
        }
      )

      if (newResp?.data) {
        setScoresData((prevData) => ({
          ...newResp.data,
          scores: [...prevData.scores, ...newResp.data.scores],
        }))
      }
      setLoading(false)
    },
    {
      triggerOnNoScroll: true,
      offset: 32,
    }
  )

  // Reset the data when we get something new loaded
  useEffect(() => {
    setScoresData(initialData)
  }, [initialData])

  if (!scores.length) {
    return (
      <NonIdealState
        icon="puzzle-alt"
        title={t('titles.scoresFromTemplateName', {
          name: scoresData.template.title,
        })}
        subtitle={t('content.empty.templates.subtitle')}
      />
    )
  }

  const options: ScoreViewOptions = {
    showAuthor: true,
    hideState: true,
  }

  return (
    <SelectionProvider initialItems={scores}>
      <ControlArea setViewType={setViewType} viewType={layoutType} order={order}>
        <RemoveScoresButton />
      </ControlArea>
      <div className="flex-1 overflow-y-auto relative pb-4" ref={scrollRef}>
        <ScoresView scores={scores} viewType={layoutType} options={options} />
        {remaining && loading && <LoadingSpinner />}
      </div>
    </SelectionProvider>
  )
}

export default TemplateCopiesView
